import { type ReactNode } from 'react';

import { Box, Grid } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

/** Closes to nothing by animating its height, and opens again. A closed row stays mounted, so what it holds keeps
 * running, but none of it can be reached. `held` makes it wait a second before it closes, long enough to read what
 * it shows. `onCollapsed` is called when it has closed. */
export const CollapsingRow = ({
  collapsed,
  held = false,
  onCollapsed,
  className,
  children,
}: {
  collapsed: boolean;
  held?: boolean;
  onCollapsed?: () => void;
  className?: string;
  children: ReactNode;
}) => (
  <Grid
    aria-hidden={collapsed}
    inert={collapsed}
    className={cn(
      'transition-[grid-template-rows,opacity] duration-200 ease-out',
      collapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr]',
      collapsed && held && 'delay-1000',
      className
    )}
    onTransitionEnd={(event) => {
      const isHeight =
        event.target === event.currentTarget &&
        event.propertyName === 'grid-template-rows';
      if (collapsed && isHeight) onCollapsed?.();
    }}
  >
    <Box className="min-h-0 overflow-hidden">{children}</Box>
  </Grid>
);
